<script setup lang="ts">
  import { watch, ref, computed, onMounted, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { CheckCircle2, Gauge, Info, ShieldCheck, UserCheck } from "lucide-vue-next";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useQualityChecksStore } from "@stores/qualityChecks";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown, type FilterConfig } from "@/components/episto-ui/filter-dropdown";
  import { DataTable, type DataTableColumn } from "@/components/episto-ui/data-table";
  import { Select, type SelectOption } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Tooltip } from "@/components/ui/tooltip";
  import { durationFrom } from "@/utils/time";
  import QualityDetailPanel from "./QualityDetailPanel.vue";
  import { DIMENSIONS, VERDICT_LABEL, FINAL_LABEL, finalFor, verdictVariant, lastFour } from "./quality";
  import type { QualityCheckRow, QualityDecision, QualityDimension } from "@/types";

  type ColumnKind = "select" | "id" | "token" | "duration" | "verdict" | "triBoolean" | "final" | "score";

  interface QualityColumn extends DataTableColumn<QualityCheckRow> {
    key: string;
    width?: number;
    kind: ColumnKind;
  }

  const visibleDimensions = computed(() => DIMENSIONS.filter(({ key }) => key !== "substance" || isAiChatSurvey.value));

  const columns = computed<QualityColumn[]>(() => [
    { key: "select", label: "", width: 44, kind: "select" },
    { key: "id", label: "ID", width: 90, kind: "id" },
    { key: "token", label: "Token", width: 90, kind: "token" },
    { key: "time_spent", label: "Time spent", width: 116, kind: "duration", sortable: true },
    { key: "verdict", label: "Quality (auto)", width: 120, kind: "verdict" },
    { key: "is_valid_quality_manual", label: "Quality (manual)", width: 130, kind: "triBoolean" },
    { key: "final", label: "Quality (final)", width: 120, kind: "final" },
    ...visibleDimensions.value.map(({ key, label }) => ({
      key,
      label,
      width: key === "appropriateness" ? 144 : 122,
      kind: "score" as const,
    })),
  ]);

  const MANUAL_OPTIONS: SelectOption[] = [
    { value: "empty", label: "–" },
    { value: "true", label: "OK" },
    { value: "false", label: "NOK" },
  ];

  const AUTO_FILTER_OPTIONS = [
    { label: "OK", value: "ok" },
    { label: "Review", value: "review" },
    { label: "NOK", value: "nok" },
  ];
  const MANUAL_FILTER_OPTIONS = [
    { label: "–", value: "empty" },
    { label: "OK", value: "ok" },
    { label: "NOK", value: "nok" },
  ];
  const FINAL_FILTER_OPTIONS = [
    { label: "OK", value: "ok" },
    { label: "NOK", value: "nok" },
  ];
  const SCORE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ label: String(n), value: n }));

  const store = useQualityChecksStore();
  const { rows, currentPage, totalPages, loading, searchQuery, sortColumn, sortDirection, autoFilter, manualFilter, finalFilter, scoreFilters, activeFiltersCount } = storeToRefs(store);
  const { token: surveyToken, isAiChatSurvey } = storeToRefs(useSurveyStore());

  const selectedCustomer = ref<QualityCheckRow | null>(null);
  const isPanelOpen = computed({
    get: () => !!selectedCustomer.value,
    set: (value: boolean) => {
      if (!value) selectedCustomer.value = null;
    },
  });
  const handleSelect = (item: QualityCheckRow) => {
    selectedCustomer.value = item;
  };

  const filterConfigs = computed<FilterConfig[]>(() => [
    {
      id: "auto",
      label: "Quality (auto)",
      icon: ShieldCheck,
      hasSearch: false,
      options: AUTO_FILTER_OPTIONS,
      selectedValues: autoFilter.value,
    },
    {
      id: "manual",
      label: "Quality (manual)",
      icon: UserCheck,
      hasSearch: false,
      options: MANUAL_FILTER_OPTIONS,
      selectedValues: manualFilter.value,
    },
    {
      id: "final",
      label: "Quality (final)",
      icon: CheckCircle2,
      hasSearch: false,
      options: FINAL_FILTER_OPTIONS,
      selectedValues: finalFilter.value,
    },
    ...visibleDimensions.value.map(({ key, label }) => ({
      id: key,
      label,
      icon: Gauge,
      hasSearch: false,
      options: SCORE_OPTIONS,
      selectedValues: scoreFilters.value[key] as (string | number | boolean)[],
    })),
  ]);

  const filterValues = (filterId: string): (string | number)[] | undefined => {
    if (filterId === "auto") return autoFilter.value;
    if (filterId === "manual") return manualFilter.value;
    if (filterId === "final") return finalFilter.value;
    return scoreFilters.value[filterId as QualityDimension];
  };
  const handleAnyFilterChange = (filterId: string, value: string | number | boolean) => {
    const values = filterValues(filterId) as (string | number | boolean)[] | undefined;
    if (!values) return;
    const index = values.indexOf(value);
    if (index >= 0) values.splice(index, 1);
    else values.push(value);
  };
  const handleClearAll = () => {
    autoFilter.value = [];
    manualFilter.value = [];
    finalFilter.value = [];
    Object.values(scoreFilters.value).forEach((values) => values.splice(0));
  };

  const dimension = (item: QualityCheckRow, key: string) => item.quality?.scores[key as QualityDimension];

  const selectedIds = ref<Set<number>>(new Set());
  const selectionCount = computed(() => selectedIds.value.size);
  const allSelected = computed(() => rows.value.length > 0 && rows.value.every((row) => selectedIds.value.has(row.id)));
  const toggleRow = (id: number) => {
    const next = new Set(selectedIds.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedIds.value = next;
  };
  const toggleAll = () => {
    selectedIds.value = allSelected.value ? new Set() : new Set(rows.value.map((row) => row.id));
  };

  const propagateDecision = (item: QualityCheckRow, value: string | number | number[] | string[] | null) => {
    const decision: QualityDecision = value === "true" ? "ok" : value === "false" ? "nok" : "empty";
    const ids =
      selectedIds.value.has(item.id) && selectedIds.value.size > 1 ? [...selectedIds.value] : [item.id];
    store.decide(ids, decision);
  };

  const manualToString = (value: boolean | null): string | null => (value === null ? null : String(value));

  const fetchPage = (page = 1) => store.fetch(surveyToken.value, page);
  const handleSort = (column: string) => store.toggleSort(column);

  watch(surveyToken, (token) => token && fetchPage(1));
  onMounted(() => surveyToken.value && fetchPage(1));
  onUnmounted(() => store.reset());
</script>

<template>
  <IndexPageLayout title="Quality checks">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search by ID, PSID or token...">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleAnyFilterChange"
            @clear="handleClearAll" />
          <span
            v-if="selectionCount > 0"
            class="mr-2 text-sm text-muted-foreground">
            {{ selectionCount }} selected · edits apply to all
          </span>
        </template>
      </IndexPageFilters>
    </template>

    <DataTable
      :items="rows"
      :columns="columns"
      :current-page="currentPage"
      :total-pages="totalPages"
      :loading="loading"
      :sort-column="sortColumn"
      :sort-direction="sortDirection"
      item-key="id"
      fixed-widths
      height="calc(100vh - 320px)"
      empty-message="No complete respondents found."
      @page-change="fetchPage"
      @sort="handleSort"
      @row-click="handleSelect">
      <template #header-select-actions>
        <Checkbox
          :model-value="allSelected"
          aria-label="Select all"
          @click.stop
          @update:model-value="toggleAll" />
      </template>

      <template
        v-for="{ key, definition } in visibleDimensions"
        :key="`header-${key}`"
        #[`header-${key}-actions`]>
        <Tooltip :content="definition">
          <Info class="h-3.5 w-3.5 shrink-0 text-muted-foreground cursor-help" />
        </Tooltip>
      </template>

      <template
        v-for="column in columns"
        :key="column.key"
        #[`cell-${column.key}`]="{ item }">
        <Checkbox
          v-if="column.kind === 'select'"
          :model-value="selectedIds.has(item.id)"
          :aria-label="`Select ${item.id}`"
          @click.stop
          @update:model-value="toggleRow(item.id)" />

        <span
          v-else-if="column.kind === 'id'"
          class="line-clamp-1 font-medium text-primary">
          #{{ item.id }}
        </span>

        <span
          v-else-if="column.kind === 'token'"
          class="line-clamp-1 font-medium text-primary">
          #{{ lastFour(item.token) }}
        </span>

        <span
          v-else-if="column.kind === 'duration'"
          class="tabular-nums"
          :class="item.time_spent === null ? 'text-muted-foreground/50' : 'text-muted-foreground'">
          {{ item.time_spent === null ? "–" : durationFrom(item.time_spent) }}
        </span>

        <template v-else-if="column.kind === 'verdict'">
          <span
            v-if="!item.quality"
            class="text-muted-foreground/50">
            –
          </span>
          <Badge
            v-else
            :variant="verdictVariant(item.quality.verdict)">
            {{ VERDICT_LABEL[item.quality.verdict] }}
          </Badge>
        </template>

        <template v-else-if="column.kind === 'final'">
          <Badge :variant="finalFor(item) === 'ok' ? 'success' : 'destructive'">
            {{ FINAL_LABEL[finalFor(item)] }}
          </Badge>
        </template>

        <template v-else-if="column.kind === 'score'">
          <span
            v-if="!dimension(item, column.key)"
            class="text-muted-foreground/50">
            –
          </span>
          <Tooltip
            v-else
            :content="dimension(item, column.key)!.justification">
            <Badge
              v-if="dimension(item, column.key)!.score <= 3"
              :variant="dimension(item, column.key)!.score <= 2 ? 'destructive' : 'warning'">
              {{ dimension(item, column.key)!.score }}
            </Badge>
            <span
              v-else
              class="tabular-nums text-muted-foreground">
              {{ dimension(item, column.key)!.score }}
            </span>
          </Tooltip>
        </template>

        <Select
          v-else-if="column.kind === 'triBoolean'"
          :model-value="manualToString(item.is_valid_quality_manual)"
          :options="MANUAL_OPTIONS"
          placeholder=""
          clearable
          @click.stop
          @update:model-value="propagateDecision(item, $event)" />
      </template>
    </DataTable>

    <template #dialogs>
      <QualityDetailPanel
        v-model:open="isPanelOpen"
        :customer="selectedCustomer" />
    </template>
  </IndexPageLayout>
</template>
